/**
 * Detects "in-app" browsers -- the embedded WebViews that social apps
 * (Facebook, Instagram, TikTok, ...) open links in, instead of the phone's real
 * browser.
 *
 * Why this matters here: production Sentry data shows that photo uploads which
 * fail as a WHOLE BATCH (every file, status 0, file bytes unreadable at upload
 * time) happen almost exclusively inside the Facebook in-app browser. Regular
 * browsers only ever drop the odd file. The in-app WebView reclaims/suspends the
 * picked files mid-upload, so the customer loses the entire order. The reliable
 * remedy is to get them into Chrome/Safari before they upload, so we detect the
 * WebView and nudge them.
 *
 * Detection is by user-agent substring. It is deliberately conservative: only
 * apps with an unambiguous UA marker are flagged, so a normal mobile browser is
 * never told to "switch browser". A bare Android WebView (`; wv`) is NOT flagged
 * on its own -- too many legitimate apps embed one -- unless a known social
 * marker is also present.
 */

export type InAppBrowserApp =
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'snapchat'
  | 'pinterest'
  | 'linkedin'
  | 'line'
  | 'other';

export interface InAppBrowserInfo {
  isInApp: boolean;
  app: InAppBrowserApp | null;
}

// Ordered: check the specific social markers first.
const MARKERS: Array<{ app: InAppBrowserApp; test: RegExp }> = [
  // Facebook: FBAN/FBAV/FB_IAB (Android), FBIOS/FBDV/FBSV (iOS).
  { app: 'facebook', test: /\bFB(AN|AV|_IAB|IOS|DV|SV|SN|MD|CR|LC|_OR)\b|FB_IAB/i },
  { app: 'instagram', test: /\bInstagram\b/i },
  // TikTok WebViews report musical_ly / BytedanceWebview.
  { app: 'tiktok', test: /\bmusical_ly\b|\bBytedanceWebview\b|\bTikTok\b/i },
  { app: 'snapchat', test: /\bSnapchat\b/i },
  { app: 'pinterest', test: /\bPinterest\b/i },
  { app: 'linkedin', test: /\bLinkedInApp\b/i },
  { app: 'line', test: /\bLine\//i },
];

/**
 * Classify a user-agent string. Pure: pass `navigator.userAgent` (or '' on the
 * server) so it can be unit-tested without a browser.
 */
export function detectInAppBrowser(userAgent: string | undefined | null): InAppBrowserInfo {
  const ua = userAgent || '';
  if (!ua) return { isInApp: false, app: null };
  for (const { app, test } of MARKERS) {
    if (test.test(ua)) return { isInApp: true, app };
  }
  return { isInApp: false, app: null };
}

/** Browser-side convenience wrapper. Safe to call during SSR (returns false). */
export function detectCurrentInAppBrowser(): InAppBrowserInfo {
  if (typeof navigator === 'undefined') return { isInApp: false, app: null };
  return detectInAppBrowser(navigator.userAgent);
}

export type MobilePlatform = 'android' | 'ios' | 'other';

/** Which phone OS the user agent names; the open-in-browser route differs. */
export function mobilePlatform(userAgent: string | undefined | null): MobilePlatform {
  const ua = userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod|FBIOS/i.test(ua)) return 'ios';
  return 'other';
}

/**
 * The URL to carry into the real browser. Chrome/Safari share no cookies or
 * storage with the in-app WebView, so the cart has to travel in the URL: the
 * order id and guest token are added (the URL store order-session.ts reads on
 * landing) unless the URL already names a token — then it is left alone, so
 * the halves of two different sessions are never mixed. Half a session adds
 * nothing. The preview flag and the hash are dropped.
 */
export function handoffUrl(
  href: string,
  session: { orderId?: string | null; guestToken?: string | null },
): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  url.searchParams.delete('inappPreview');
  url.hash = '';
  const hasToken =
    url.searchParams.has('guest_session_token') || url.searchParams.has('guestToken');
  if (!hasToken && session.orderId && session.guestToken) {
    url.searchParams.set('orderId', session.orderId);
    url.searchParams.set('guest_session_token', session.guestToken);
  }
  return url.toString();
}

/**
 * Android's hand-off from a WebView to Chrome: an `intent://` link for the same
 * URL, which the Facebook and Instagram WebViews pass to the system. Where
 * Chrome is missing (or the app refuses the intent) the fallback URL opens
 * instead, which at worst reloads this page. Unverified on every app build —
 * some FB builds are known to block intents — so the copy-link button stays.
 */
export function androidChromeIntentUrl(href: string): string {
  const url = new URL(href);
  const scheme = url.protocol.replace(':', '');
  return (
    `intent://${url.host}${url.pathname}${url.search}` +
    `#Intent;scheme=${scheme};package=com.android.chrome;` +
    `S.browser_fallback_url=${encodeURIComponent(href)};end`
  );
}
